/**
 * K-NAMHS skip-logic gate graph — TypeScript port of the `GateGraph` pieces of
 * `MoDN/modn/scripts/experiment/min_questions/strategy.py`.
 *
 * Two ancestor sources, both DTA-clean (derived purely from the skip-logic tree
 * structure, no patient data):
 *
 *   - **Stem-sub parent chain** (`ymdq001a -> ymdq001`) — `directParent`.
 *   - **JSON gates** — for every gated feature, an operator + gating stems +
 *     firing predicate, in `gatesByFeature`.
 *
 * Loaded from the per-module `tree_{SoPh,GAD,MDD,PTSD}.json` files written by
 * `modn.scripts.visualization.skip_logic_trees` and bundled as app assets.
 *
 * NaN convention (memory: K-NAMHS consultation simulator semantics): a question
 * whose gate never fires is never posed and is treated as NaN (== `null` here),
 * which the boolean evaluator reads as `false`.
 */
import {Answer, Observed} from './boolean';

export type Gate = {
  operator: string; // any | any_speculative | all | single | manual | count_threshold | unknown
  gatingStems: string[];
  predicate: string; // "Yes" | "≠No" | "mixed" | a specific category label
};

export type GateGraph = {
  directParent: Map<string, string>;
  gatesByFeature: Map<string, Gate[]>;
};

// Shape of a `tree_*.json` document (only the fields we read).
export type TreeDoc = {
  nodes?: Record<string, {is_gate?: boolean; parent?: string | null}>;
  gates?: Array<{
    operator?: string;
    gating_stems?: string[];
    predicate?: string;
    gated_features?: string[];
  }>;
};

/** A parent answer that blocks its follow-up: "No" or unanswered/NaN.
 * Mirrors `strategy._is_blocking` / the stem-sub block rule. */
export const isBlocking = (value: Answer | undefined): boolean =>
  value === 'No' ||
  value === null ||
  value === undefined ||
  (typeof value === 'number' && Number.isNaN(value));

/** Build a merged `GateGraph` from per-module tree JSON docs.
 * Mirrors `strategy.load_gate_graph`. */
export const loadGateGraph = (docs: TreeDoc[]): GateGraph => {
  const directParent = new Map<string, string>();
  const gatesByFeature = new Map<string, Gate[]>();

  for (const doc of docs) {
    const nodes = doc.nodes ?? {};
    for (const [name, node] of Object.entries(nodes)) {
      if (node.is_gate) {
        continue;
      }
      const jparent = node.parent;
      if (jparent == null || jparent.startsWith('_gate_')) {
        continue;
      }
      if (!directParent.has(name)) {
        directParent.set(name, jparent);
      }
    }
    for (const gateDoc of doc.gates ?? []) {
      const gate: Gate = {
        operator: gateDoc.operator ?? 'unknown',
        gatingStems: gateDoc.gating_stems ?? [],
        predicate: gateDoc.predicate ?? 'Yes',
      };
      for (const f of gateDoc.gated_features ?? []) {
        const list = gatesByFeature.get(f);
        if (list) {
          list.push(gate);
        } else {
          gatesByFeature.set(f, [gate]);
        }
      }
    }
  }

  return {directParent, gatesByFeature};
};

/** Does `observed[stem]` match the gate's firing predicate?
 * Mirrors `strategy._stem_matches_predicate`. */
export const stemMatchesPredicate = (
  observed: Observed,
  stem: string,
  predicate: string,
): boolean => {
  if (!observed.has(stem)) {
    return false;
  }
  const v = observed.get(stem) as Answer;
  if (v === null || (typeof v === 'number' && Number.isNaN(v))) {
    return false;
  }
  if (predicate === 'Yes') {
    return v === 'Yes';
  }
  if (predicate === '≠No' || predicate === 'mixed') {
    return v !== 'No';
  }
  // Specific category label, including "No" for branch-style gates.
  return v === predicate;
};

/** `true` iff the gate cannot fire given only `observed` answers.
 * Mirrors `strategy._gate_definitely_failed`. */
const gateDefinitelyFailed = (gate: Gate, observed: Observed): boolean => {
  const {operator: op, gatingStems: stems, predicate: pred} = gate;
  if (op === 'any' || op === 'any_speculative') {
    if (!stems.every(s => observed.has(s))) {
      return false;
    }
    return !stems.some(s => stemMatchesPredicate(observed, s, pred));
  }
  if (op === 'all') {
    for (const s of stems) {
      if (observed.has(s) && !stemMatchesPredicate(observed, s, pred)) {
        return true;
      }
    }
    return false;
  }
  if (op === 'single') {
    if (stems.length === 0) {
      return true;
    }
    const s = stems[0];
    if (!observed.has(s)) {
      return false;
    }
    return !stemMatchesPredicate(observed, s, pred);
  }
  return false; // manual / count_threshold / unknown — undecidable here
};

/** Atoms plus every transitive gate-stem ancestor.
 * Mirrors `strategy._ancestor_universe`. */
const ancestorUniverse = (atomFeatures: Set<string>, gates: GateGraph): Set<string> => {
  const universe = new Set<string>(atomFeatures);
  const queue: string[] = Array.from(atomFeatures);
  while (queue.length > 0) {
    const f = queue.pop() as string;
    const p = gates.directParent.get(f);
    if (p != null && !universe.has(p)) {
      universe.add(p);
      queue.push(p);
    }
    for (const g of gates.gatesByFeature.get(f) ?? []) {
      for (const s of g.gatingStems) {
        if (!universe.has(s)) {
          universe.add(s);
          queue.push(s);
        }
      }
    }
  }
  return universe;
};

/**
 * Augment `observed` with NaN (`null`) for atoms whose gate has definitively
 * failed, iterating to a fixed point. Mirrors `strategy.close_observed_with_gates`.
 * Returns a new map; `observed` is not mutated.
 */
export const closeObservedWithGates = (
  observed: Observed,
  atomFeatures: Set<string>,
  gates: GateGraph,
): Observed => {
  const universe = ancestorUniverse(atomFeatures, gates);
  const closed: Observed = new Map(observed);
  for (;;) {
    let changed = false;
    for (const q of universe) {
      if (closed.has(q)) {
        continue;
      }
      // 1. Direct stem-sub edge — block only when parent answered "No"/NaN.
      const p = gates.directParent.get(q);
      if (p != null && closed.has(p)) {
        if (isBlocking(closed.get(p))) {
          closed.set(q, null);
          changed = true;
          continue;
        }
      }
      // 2. JSON gates — any definitively-failed gate kills the atom.
      for (const gate of gates.gatesByFeature.get(q) ?? []) {
        if (gateDefinitelyFailed(gate, closed)) {
          closed.set(q, null);
          changed = true;
          break;
        }
      }
    }
    if (!changed) {
      return closed;
    }
  }
};
